<template>
  <v-card outlined>
    <v-card-title class="text-subtitle-1">
      Kundeninformationen
    </v-card-title>
    
    <v-card-text>
      <v-row dense>
        <v-col cols="6">
          <v-switch
            :input-value="normalizedIsCustomer"
            :true-value="'1'"
            :false-value="'0'"
            label="Ist Kunde"
            @change="handleIsCustomerChange($event)"
            :loading="switchLoading"
          ></v-switch>
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="localCustomer.isInterest"
            :items="computedInterestOptions"
            item-text="text"
            item-value="value"
            label="Gesprächstatus"
            outlined
            dense
            @change="updateField('isInterest', localCustomer.isInterest)"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Display why not customer info if exists -->
       <v-alert
        v-if="customer.is_customer_reason && normalizedIsCustomer === '0'"
        type="info"
        dense
        class="text-wrap"
        style="word-break: break-word; white-space: normal;"
      >
        <div class="d-flex flex-column">
          <div>
            <strong>Grund (kein Kunde):</strong>
            <span class="reason-text">{{ customer.is_customer_reason }}</span>
          </div>
          <div class="">
            <small>Erfasst von {{ customer.is_customer_changed_by }} am {{ formatDate(customer.is_customer_changed_date) }}</small>
          </div>
        </div>
      </v-alert>


      <v-divider class="my-2"></v-divider>
      <v-row>
        <v-col cols="12" class="text-right">
          <v-btn 
            icon 
            small 
            @click="toggleEditMode"
            :title="isEditing ? 'Änderungen speichern' : 'Kundeninformationen bearbeiten'"
            :color="isEditing ? 'primary' : ''"
          >
            <v-icon small>{{ isEditing ? 'mdi-check' : 'mdi-pencil' }}</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <div class="scrollable-content">
        <!-- Contact Information -->
        <div class="mb-2">
          <strong v-if="!isEditing">Adresse:</strong><br>
          <template v-if="isEditing">
            <v-text-field
              v-model="editData.street"
              label="Straße"
              dense
              outlined
            ></v-text-field>
            <v-row dense>
              <v-col cols="4">
                <v-text-field
                  v-model="editData.zipcode"
                  label="PLZ"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  v-model="editData.city"
                  label="Stadt"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            {{ customer.street }}<br>
            {{ customer.zipcode }} {{ customer.city }}
          </template>
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Telefon:</strong> 
          <template v-if="isEditing">
            <v-text-field
              v-model="editData.phone"
              label="Telefon"
              dense
              outlined
            ></v-text-field>
          </template>
          <template v-else>
            <a v-if="customer.phone" :href="`tel:${formatPhoneForLink(customer.phone)}`" class="phone-link">
              {{ customer.phone }}
            </a>
            <span v-else>-</span>
          </template>
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Email:</strong> 
          <template v-if="isEditing">
            <v-text-field
              v-model="editData.email"
              label="Email"
              dense
              outlined
            ></v-text-field>
          </template>
          <template v-else>
            <a v-if="customer.email" :href="`mailto:${customer.email}`" class="email-link">
              {{ customer.email }}
            </a>
            <span v-else>-</span>
          </template>
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Kundengruppe:</strong>
          <template v-if="isEditing">
            <v-select
              v-model="editData.group_main_text"
              :items="groupMainOptions"
              label="Kundengruppe"
              outlined
              dense
            ></v-select>
          </template>
          <template v-else>
            {{ customer.group_main_text || '-' }}
          </template>
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Untergruppe:</strong>
          <template v-if="isEditing">
            <v-select
              v-model="editData.group_type_text"
              :items="groupTypeOptions"
              label="Untergruppe"
              outlined
              dense
            ></v-select>
          </template>
          <template v-else>
            {{ customer.group_type_text || '-' }}
          </template>
        </div>
        <div class="mb-2">
          <strong v-if="!isEditing">Kundentyp:</strong>
          <template v-if="isEditing">
            <v-select
              v-model="editData.workshop_type_name"
              :items="WorkshopTypeOptions"
              label="Kundentyp"
              outlined
              dense
            ></v-select>
          </template>
          <template v-else>
            {{ customer.workshop_type_name || '-' }}
          </template>
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Vertragswerkstatt:</strong>
          <template v-if="isEditing">
            <v-select
              v-model="editData.authorized_service_partner_name"
              :items="groupAutoServiceOptions"
              label="Vertragswerkstatt"
              outlined
              dense
            ></v-select>
          </template>
          <template v-else>
            {{ customer.authorized_service_partner_name || '-' }}
          </template>
        </div>

        <div class="mb-2">
          <strong>Letzte Abholung:</strong> {{ customer.last_pickup || '-' }}
        </div>

        <div class="mb-2">
          <strong v-if="!isEditing">Bemerkung:</strong><br>
          <template v-if="isEditing">
            <v-textarea
              v-model="editData.note"
              label="Bemerkung"
              rows="3"
              outlined
              dense
            ></v-textarea>
          </template>
          <template v-else>
            {{ customer.note || '-' }}
          </template>
        </div>

        <!-- Recruitment Data Section -->
        <div v-if="customer.isCustomer === '0'" class="my-2 recruitment-section">
          <div class="mb-2">
            <strong v-if="!isEditing">Entsorgungsfirma:</strong> 
            <template v-if="isEditing">
              <v-text-field
                v-model="editData.current_disposal_company"
                label="Aktuelle Entsorgungsfirma"
                dense
                outlined
              ></v-text-field>
            </template>
            <template v-else>
              {{ customer.current_disposal_company || '-' }}
            </template>
          </div>

          <div class="mb-2">
            <strong v-if="!isEditing">Aktueller Preis:</strong> 
            <template v-if="isEditing">
              <v-text-field
                v-model="editData.current_price"
                label="Aktueller Preis"
                dense
                outlined
              ></v-text-field>
            </template>
            <template v-else>
              {{ customer.current_price || '-' }}
            </template>
          </div>

          <div class="mb-2">
            <strong v-if="!isEditing">Reifen pro Monat:</strong> 
            <template v-if="isEditing">
              <v-text-field
                v-model="editData.tires_per_month"
                label="Reifen pro Monat"
                dense
                outlined
              ></v-text-field>
            </template>
            <template v-else>
              {{ customer.tires_per_month || '-' }}
            </template>
          </div>
        </div>
      </div>

      <!-- Why Not Customer Dialog -->
      <v-dialog v-model="showWhyNotCustomerDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">
            Grund für Kundenstatus-Änderung
          </v-card-title>
          <v-card-text>
            <p class="mb-4">Bitte geben Sie den Grund an, warum dieser Kontakt kein Kunde mehr ist:</p>
            <v-textarea
              v-model="is_customer_reason"
              label="Grund"
              outlined
              rows="3"
              placeholder="z.B. Kunde hat gekündigt, Preise zu hoch, Service nicht zufriedenstellend..."
              :rules="[v => !!v || 'Bitte geben Sie einen Grund an']"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelWhyNotCustomer">Abbrechen</v-btn>
            <v-btn color="primary" @click="confirmWhyNotCustomer" :disabled="!is_customer_reason">
              Bestätigen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Convert to Customer Dialog -->
      <v-dialog v-model="showConvertToCustomerDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">
            <v-icon left color="primary">mdi-check-circle</v-icon>
            Kunde bestätigen
          </v-card-title>
          <v-card-text>
            <p class="mb-3">
              <strong>{{ customer.company || customer.lastname || 'Dieser Kontakt' }}</strong> wird jetzt als Kunde markiert.
            </p>
            <p class="mb-0">
              Sind Sie sicher? Dies kann nur mit einem Grund rückgängig gemacht werden.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelConvertToCustomer">Nein, abbrechen</v-btn>
            <v-btn color="primary" @click="confirmConvertToCustomer">Ja, bestätigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'CustomerInfo',

  props: {
    customer: {
      type: Object,
      required: true
    },
    interestOptions: {
      type: Array,
      required: true
    },
    conservationStatuses: {
      type: Array,
      default: () => []
    },
    currentEmployee: {
      type: String,
      default: 'Unknown Employee'
    }
  },

  data() {
    return {
      isEditing: false,
      editData: {},
      localCustomer: {},
      showWhyNotCustomerDialog: false,
      is_customer_reason: '',
      pendingIsCustomerValue: null,
      groupMainOptions: [],  
      groupTypeOptions: [],
      WorkshopTypeOptions: [],
      groupAutoServiceOptions: [],
      localIsCustomer: null,
      switchLoading: false,
      showConvertToCustomerDialog: false,
      pendingConvertToCustomerValue: null
    }
  },

  computed: {
    normalizedIsCustomer() {
      if (this.localIsCustomer !== null) {
        return this.localIsCustomer
      }
      if (!this.customer || this.customer.isCustomer === undefined || this.customer.isCustomer === null) {
        return '0'
      }
      return String(this.customer.isCustomer)
    },

    computedInterestOptions() {
      if (this.conservationStatuses && this.conservationStatuses.length > 0) {
        return this.conservationStatuses.map(status => ({
          text: status.label,
          value: status.keyword,
          color: status.color
        }))
      }
      
      return this.interestOptions
    },

    currentUsername() {
      return localStorage.getItem('username') || this.currentEmployee || 'Unknown User'
    }
  },

  watch: {
    customer: {
      handler(newVal) {
        this.localCustomer = { ...newVal }
        this.localIsCustomer = null
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    this.loadGroupMain()
    this.loadGroupTypes()
    this.loadWorkshopTypes()
    this.loadGroupAutoService()
  },

  methods: {
    async handleIsCustomerChange(newValue) {
      const oldValue = this.normalizedIsCustomer

      if (oldValue === newValue) {
        return
      }

      this.switchLoading = true
      this.localIsCustomer = newValue
      
      try {
        // If changing from non-customer (0) to customer (1), show simple confirmation
        if (oldValue === '0' && newValue === '1') {
          this.pendingConvertToCustomerValue = newValue
          this.showConvertToCustomerDialog = true
          return
        }
        
        // If changing from customer (1) to non-customer (0), show dialog for reason
        if (oldValue === '1' && newValue === '0') {
          this.pendingIsCustomerValue = newValue
          this.is_customer_reason = ''
          this.showWhyNotCustomerDialog = true
        } 
        else {
          this.updateFieldImmediately('isCustomer', newValue)
        }
      } finally {
        this.switchLoading = false
      }
    },

    confirmWhyNotCustomer() {
      if (this.is_customer_reason) {
        const currentDate = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
        const updateData = {
          isCustomer: this.pendingIsCustomerValue,
          is_customer_reason: this.is_customer_reason,
          is_customer_changed_by: this.currentUsername,
          is_customer_changed_date: currentDate,
          customer_before: 'yes'
        }

        this.$emit('update', updateData)
        this.showWhyNotCustomerDialog = false
        this.pendingIsCustomerValue = null
        this.is_customer_reason = ''
      }
    },

    cancelWhyNotCustomer() {
      this.localIsCustomer = '1' 
      this.showWhyNotCustomerDialog = false
      this.pendingIsCustomerValue = null
      this.is_customer_reason = ''
  
      this.$forceUpdate()
    },


    confirmConvertToCustomer() {
      
      console.log('Customer converted:', {
        customerId: this.customer.id,
        customerName: this.customer.company || this.customer.lastname,
        convertedBy: this.currentUsername,
        timestamp: new Date().toISOString()
      })

      // Update the field
      this.updateFieldImmediately('isCustomer', this.pendingConvertToCustomerValue)
      
      // Reset
      this.showConvertToCustomerDialog = false
      this.pendingConvertToCustomerValue = null
    },

    cancelConvertToCustomer() {
      // Reset to previous state
      this.localIsCustomer = '0'
      this.showConvertToCustomerDialog = false
      this.pendingConvertToCustomerValue = null
      
      this.$forceUpdate()
    },

    updateFieldImmediately(field, value) {
      const updateData = {}
      updateData[field] = value

      if (field === 'isCustomer' && value === '0' && this.normalizedIsCustomer === '1') {
        updateData.customer_before = 'yes'
      }
      this.$emit('update', updateData)
    },

    updateField(field, value) {
      const updateData = {}
      updateData[field] = value
      this.$emit('update', updateData)
    },

    toggleEditMode() {
      if (this.isEditing) {
        this.saveChanges()
      } else {
        this.isEditing = true
        this.resetEditData()
      }
    },

    resetEditData() {
      const { isCustomer, isInterest, ...editableFields } = this.customer
      this.editData = { ...editableFields }
    },

    cancelEdit() {
      this.isEditing = false
      this.resetEditData()
    },

    saveChanges() {
      this.$emit('update', this.editData)
      this.isEditing = false
    },

    formatPhoneForLink(phone) {
      if (!phone) return ''
      return phone.replace(/[^\d+]/g, '')
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('de-DE')
      } catch (error) {
        return dateString
      }
    },
    
    async loadGroupMain() {
      const param = {
        table: "group_main",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        if (Array.isArray(result)) {
          this.groupMainOptions = result.map(g => g.name || "Unknown Group")
            .filter(name => name !== "Unknown Group")
            .sort((a, b) => a.localeCompare(b))
        }
      } catch (error) {
        console.error('Error fetching groups:', error)
      }
    },

    async loadGroupTypes() {
      const param = {
        table: "group_type",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        this.groupTypeOptions = Array.isArray(result) ? 
          result.map(g => g.name || "Unknown Type")
            .filter(name => name !== "Unknown Type")
            .sort((a, b) => a.localeCompare(b)) : []
      } catch(error) {
        console.error('Error fetching group types:', error) 
      }
    },
    async loadWorkshopTypes() {
      const param = {
        table: "group_workshop_type",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        console.log('-- Workshop Types fetched----:', result)
        this.WorkshopTypeOptions = Array.isArray(result) ? 
          result.map(g => g.name || "Unknown Type")
            .filter(name => name !== "Unknown Type")
            .sort((a, b) => a.localeCompare(b)) : []
      } catch(error) {
        console.error('Error fetching workshop types:', error) 
      }
    },
    async loadGroupAutoService() {
      const param = {
        table: "group_authorized_service_partner",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        this.groupAutoServiceOptions = Array.isArray(result) ? 
          result.map(g => g.name || "Unknown Type")
            .filter(name => name !== "Unknown Type")
            .sort((a, b) => a.localeCompare(b)) : []
      } catch(error) {
        console.error('Error fetching authorized service partners:', error) 
      }
    },

  }

}
</script>

<style scoped>
.phone-link,
.email-link {
  text-decoration: none;
  color: #1976d2;
}

.phone-link:hover,
.email-link:hover {
  text-decoration: underline;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-top: 8px;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.recruitment-section {
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  border-left: 3px solid #1976d2;
}

.reason-text {
  word-break: break-word;
  white-space: normal;
  display: inline;
}

:deep(.v-alert__content) {
  word-break: break-word;
  white-space: normal;
}
</style>